import { useEffect, useRef, useState } from 'react';
import { Card } from '../../../components/ui';
import { banner as bannerApi, type BannerAppearance, type BannerPreviewConfig, type BannerTemplateIssue } from '../../../lib/api';
import { LANG_NAMES } from '../../../lib/format';
import type { Language } from './TextCard';

/**
 * The banner as a visitor will see it: a sample page served by the web image
 * at /demo/preview.html runs the real banner script and takes the draft
 * settings over postMessage. Nothing leaves this origin.
 *
 * Protocol: the page posts {type:'wekalp:preview:ready'}; the iframe's own load
 * event counts as ready too, since that message can arrive before the listener
 * is attached; the console answers
 * with {type:'wekalp:preview:config', config} (the widget config) and then
 * {type:'wekalp:preview:update', appearance, lang, state} on every change.
 * After each render the page posts {type:'wekalp:preview:check', issues} with
 * what it measured (button sizes, visibility, contrast, overflow); the list is
 * shown under the frame and handed to the parent, where it gates Publish.
 */

const PREVIEW_PATH = '/demo/preview.html';
const DEVICES = { desktop: { label: 'Desktop', width: 1024, height: 640 }, phone: { label: 'Phone', width: 390, height: 720 } } as const;
type Device = keyof typeof DEVICES;
type VisitState = 'first' | 'returning';

export default function Preview({ pointId, appearance, lang, languages, onLang, onLanguages, onIssues }: {
  pointId: string;
  appearance: BannerAppearance;
  lang: string;
  languages: Language[];
  onLang: (code: string) => void;
  onLanguages: (languages: Language[]) => void;
  /** The measured issues after each render; an empty list when the render is clean. */
  onIssues?: (issues: BannerTemplateIssue[]) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [state, setState] = useState<VisitState>('first');
  const [served, setServed] = useState<boolean | null>(null);
  const [ready, setReady] = useState(false);
  const [config, setConfig] = useState<BannerPreviewConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [checks, setChecks] = useState<BannerTemplateIssue[]>([]);
  const issuesCb = useRef(onIssues);
  issuesCb.current = onIssues;

  // A new point starts clean until its first render reports.
  useEffect(() => { setChecks([]); issuesCb.current?.([]); }, [pointId]);

  // Is the sample page there at all? In production nginx serves it beside the
  // console; a dev console without the web image shows a line instead.
  useEffect(() => {
    let cancelled = false;
    fetch(PREVIEW_PATH, { method: 'HEAD', cache: 'no-store' })
      .then((r) => { if (!cancelled) setServed(r.ok); })
      .catch(() => { if (!cancelled) setServed(false); });
    return () => { cancelled = true; };
  }, []);

  // The widget config for this point and language, from the draft.
  useEffect(() => {
    let cancelled = false;
    setConfigError(null);
    bannerApi.previewConfig(pointId, lang)
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        const langs = (c.languages ?? []).map((l) => ({ code: l.code, endonym: l.endonym }));
        if (langs.length) onLanguages(langs);
      })
      .catch((err: unknown) => { if (!cancelled) setConfigError((err as Error).message); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointId, lang]);

  // The page says when it can take messages.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      if (e.data?.type === 'wekalp:preview:ready') setReady(true);
      if (e.data?.type === 'wekalp:preview:check') {
        const raw: unknown = e.data.issues;
        const issues = (Array.isArray(raw) ? raw : [])
          .filter((i): i is { code: string; message: string } => !!i && typeof i === 'object' && typeof (i as { message?: unknown }).message === 'string')
          .map((i) => ({ code: String(i.code ?? ''), message: i.message }));
        setChecks(issues);
        issuesCb.current?.(issues);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const post = (message: Record<string, unknown>) => {
    frame.current?.contentWindow?.postMessage(message, window.location.origin);
  };

  // Config first, then the current draft, whenever the page or the config changes.
  useEffect(() => {
    if (!ready || !config) return;
    post({ type: 'wekalp:preview:config', config });
    post({ type: 'wekalp:preview:update', appearance, lang, state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, config]);

  // Every edit, debounced so a colour drag does not flood the page.
  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(() => post({ type: 'wekalp:preview:update', appearance, lang, state }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, appearance, lang, state]);

  // Scale the fixed-width page to the column.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, el.clientWidth / DEVICES[device].width));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [device]);

  const { width, height } = DEVICES[device];
  const label = (l: Language) => l.endonym ?? LANG_NAMES[l.code] ?? l.code;

  return (
    <Card title="Preview">
      <div className="ck-preview-bar">
        <div className="q-views" role="tablist" aria-label="Device">
          {(Object.keys(DEVICES) as Device[]).map((d) => (
            <button key={d} role="tab" aria-selected={device === d} className={device === d ? 'on' : ''} onClick={() => setDevice(d)}>
              {DEVICES[d].label}
            </button>
          ))}
        </div>
        <select value={lang} aria-label="Language" onChange={(e) => onLang(e.target.value)}>
          {languages.map((l) => <option key={l.code} value={l.code}>{label(l)}</option>)}
        </select>
        <select value={state} aria-label="Visitor" onChange={(e) => setState(e.target.value as VisitState)}>
          <option value="first">First visit</option>
          <option value="returning">Returning visitor</option>
        </select>
      </div>

      {configError && <div className="note warn" style={{ marginBottom: 10 }}>{configError}</div>}

      {served === false ? (
        <div className="empty">Preview is served by the web image.</div>
      ) : (
        <div ref={box} className="ck-preview-frame" style={{ height: Math.round(height * scale) }}>
          {served && (
            <iframe
              ref={frame}
              title="Banner preview"
              src={PREVIEW_PATH}
              sandbox="allow-scripts allow-same-origin"
              width={width}
              height={height}
              style={{ width, height, transform: `scale(${scale})` }}
              onLoad={() => {
                // A reload of the sample page (cache refresh, a crash) starts it empty: post again.
                setReady(true);
                if (config) {
                  post({ type: 'wekalp:preview:config', config });
                  post({ type: 'wekalp:preview:update', appearance, lang, state });
                }
              }}
            />
          )}
        </div>
      )}
      <div className="help" style={{ marginTop: 8 }}>
        {served ? (ready ? `${width} px wide` : 'Waiting for the sample page') : ''}
      </div>
      {checks.length > 0 && (
        <ul className="ck-preview-issues" aria-label="Preview issues">
          {checks.map((i, n) => <li key={`${i.code}-${n}`}>Preview: {i.message}</li>)}
        </ul>
      )}
    </Card>
  );
}
